import { z } from 'zod';

import { OWNER_ONLY_SCOPES, PERMISSION_SCOPES } from '../permissions';

import { ME_PLAN_SCOPES } from './my-work.dto';

/**
 * Personal Access Token (PAT) DTOs.
 *
 * A PAT is a long-lived bearer credential the actor creates from
 * /me/tokens and pastes into a script or external app to call the
 * Projoint API on their behalf.  The plaintext is shown ONCE on
 * creation — server only stores the SHA-256 hash and a leading
 * 24-char prefix for display + lookup.
 *
 * Scopes are pulled from the same catalogue as team-role permissions.
 * Effective permission at the edge = intersection(token.scopes,
 * actor's role permissions on the target team).  A PAT can never
 * escalate beyond what the creator already has.
 */

const PatName = z.string().min(1).max(80);
// mcp.user binds an external-agent credential to /api/mcp/user only.
export const PAT_CHANNEL_SCOPES = ['api.read', 'api.write', 'mcp.read', 'mcp.write', 'mcp.user'] as const;
const publicPatScopeSet = new Set<string>([
  ...PAT_CHANNEL_SCOPES,
  ...PERMISSION_SCOPES.filter((scope) => !OWNER_ONLY_SCOPES.has(scope)),
  // My Work (P6, docs/22 §3.14): self-only scopes a user names on purpose for
  // their own private plan; MyWorkAccessGuard / MCP projoint://me/work/today
  // still require them, and channel scopes (api.* / mcp.*) still apply.
  ...ME_PLAN_SCOPES,
]);
export const PublicPatScopeSchema = z.string().refine((scope) => publicPatScopeSet.has(scope), {
  message: 'Unknown or service-reserved PAT scope',
});
export type PublicPatScope = z.infer<typeof PublicPatScopeSchema>;

export function isPublicPatScope(scope: string): scope is PublicPatScope {
  return publicPatScopeSet.has(scope);
}

/** Projection returned by GET /me/tokens — never carries the plaintext. */
export const PersonalAccessTokenSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  /** First 24 chars of the plaintext (e.g. `pat_abc123…`) — safe to
   *  display so the user can disambiguate multiple tokens. */
  prefix: z.string(),
  scopes: z.array(z.string()),
  expiresAt: z.string().nullable(),
  lastUsedAt: z.string().nullable(),
  revokedAt: z.string().nullable(),
  createdAt: z.string(),
});
export type PersonalAccessTokenProjection = z.infer<typeof PersonalAccessTokenSchema>;

export const PersonalAccessTokenListSchema = z.array(PersonalAccessTokenSchema);

/** POST /me/tokens body. */
export const CreatePersonalAccessTokenSchema = z
  .object({
    name: PatName,
    /** Channel scopes attenuate REST/MCP before domain permissions are checked. */
    scopes: z.array(PublicPatScopeSchema).max(128).default([]),
    /** User PATs are always expiring. Service capabilities have a much shorter server-only TTL. */
    expiresInDays: z.number().int().positive().max(90),
  })
  .strict();
export type CreatePersonalAccessTokenInput = z.infer<typeof CreatePersonalAccessTokenSchema>;

/** Response from POST — uniquely carries the plaintext `token` field
 *  alongside the projection.  Plaintext is never returned again. */
export const CreatedPersonalAccessTokenSchema = PersonalAccessTokenSchema.extend({
  /** Full plaintext: `pat_<prefix>_<secret>`.  Show once, never again. */
  token: z.string(),
});
export type CreatedPersonalAccessTokenProjection = z.infer<
  typeof CreatedPersonalAccessTokenSchema
>;
